import { For, Show } from 'solid-js';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ImageComponent } from '../image-component/image-component';
import decodeHtmlEntities from '../../tools/decode-html-entities';

/* Cards top out around 320px wide (4-up on a 1270px grid). ImageComponent also fetches a 2x
   render, so this must be a width whose double is in the image service's ALLOWED_SIZES too:
   368/736 both are, in base and the stage overlay. (260/520, the pre-rebrand pair, is missing
   from stage.) */
const IMAGE_WIDTH = 368;

/* Column widths for 2-up / 3-up / 4-up rows. Flex (not grid) so a short row — the editor
   caps the block at four profiles, so most rows are short on desktop — centres instead of
   hugging the left edge. Width = (row width − gaps) / columns, gaps matching `gap-x-cvi-8`. */
const CARD_WIDTH = 'w-[calc((100%_-_var(--spacing-cvi-8))_/_2)] '
    + 'tablet:w-[calc((100%_-_2_*_var(--spacing-cvi-8))_/_3)] '
    + 'desktop:w-[calc((100%_-_3_*_var(--spacing-cvi-8))_/_4)]';

type ProfileImage = {
    url: string;
    altText: string;
    title: string;
}
type Profile = {
    image: ProfileImage;
    text: string;
    /**
     * Not emitted by the `atos/profile-images` block today — it stores a single
     * free-text field, which we render as the name. Declared so the block can
     * start sending a separate role without a frontend change.
     */
    role?: string;
}
type ProfileImagesProps = {
    profiles: Profile[];
}

const ProfileCard = (props: { profile: Profile }) => {
    const imageUrl = () => props.profile.image?.url;

    const name = () => decodeHtmlEntities(props.profile.text ?? '');
    const role = () => decodeHtmlEntities(props.profile.role ?? '');

    return (
        <li class={CARD_WIDTH}>
            <figure class="flex flex-col gap-cvi-6">
                <Show when={imageUrl()}>
                    <ImageComponent
                        src={imageUrl()!}
                        height={'AUTO'}
                        width={IMAGE_WIDTH}
                        altText={props.profile.image?.altText || ''}
                        aspectRatio={'circle'}
                        roundCorners={false}
                        spacer={false}
                    />
                </Show>

                <Show when={name() || role()}>
                    <figcaption class="flex flex-col gap-cvi-2 text-center">
                        <Show when={name()}>
                            <p class="m-0 body-lg font-cvi-bold text-cvi-fg-default">
                                {name()}
                            </p>
                        </Show>
                        <Show when={role()}>
                            <p class="m-0 body-lg font-cvi-medium text-cvi-fg-muted">{role()}</p>
                        </Show>
                    </figcaption>
                </Show>
            </figure>
        </li>
    );
};

export const ProfileImages = (props: ProfileImagesProps) => {
    // An entry with neither an image nor a caption would render an empty cell
    // and knock the rest of the grid out of alignment.
    const profiles = () => (props.profiles ?? []).filter((profile) => profile?.image?.url || profile?.text);

    return (
        <Show when={profiles().length > 0}>
            <ErrorCatcher componentName="ProfileImages">
                <ul class="m-0 flex list-none flex-wrap justify-center gap-x-cvi-8 gap-y-cvi-8 p-0 py-[var(--spacing-cvi-section-default)] tablet:gap-y-cvi-11">
                    <For each={profiles()}>
                        {(profile) => <ProfileCard profile={profile} />}
                    </For>
                </ul>
            </ErrorCatcher>
        </Show>
    );
};

ProfileImages.parseProps = (atts: any) => {
    const { profiles } = atts;
    return {
        profiles,
    };
};
